import type { RequestOptions } from './request';

export function useWorksheetAPI() {
  const { errors, request } = useRequestV2();

  async function getWorksheetsAssignedToFitter(fitterId:number) {
    const url = 'V2/Worksheet/GetWorksheetsAssignedToFitter?fitterId=' + fitterId;

    const response = await request<WorksheetResponseDTO[]>(url);

    if (response && response.HasErrors === false) {
      return mapWorksheets(response.Data);
    }

    return [];
  }

  function mapWorksheets(worksheets: WorksheetResponseDTO[]): ExtendedWorksheetFilterResponseDTO[] {
    return worksheets.map((worksheet) => {
      return {
        ProjectID: worksheet.ProjectID,
        ProjectGroupID: worksheet.ProjectGroupId,
        FitterName: worksheet.FitterName,
        StartDate: worksheet.StartDate,
        StartTime: worksheet.StartTime,
        AddressDescription: worksheet.AddressDescription,
        PersonDescription: worksheet.PersonDescription,
        FitterNote: worksheet.FitterNote,
        WorkAddress: worksheet.WorkAddress,
        WorkAddress2: worksheet.WorkAddress2,
        WorkAddressZipCode: worksheet.WorkAddressZipCode,
        WorkAddressCity: worksheet.WorkAddressCity,
        ID: worksheet.ID,
        FitterID: worksheet.FitterID,
        ProjectName: worksheet.ProjectName,
        ProjectReference: worksheet.ProjectReference,
        StatusEnum: worksheet.StatusEnum,
        WorksheetDescription: worksheet.Description,
        ContactPersonName: worksheet.ContactPersonName,
        ContactPersonPhone: worksheet.ContactPersonPhone,
        ContactPersonEmail: worksheet.ContactPersonEmail,
        IsInternalProject: worksheet.IsInternalProject,
        BlockHourRegistrations: worksheet.BlockHourRegistrations,
        CustomerIsLocked: worksheet.IsCustomerLocked,
        CustomerName: worksheet.CustomerName,
        CustomerNote: worksheet.CustomerNote,
        ProjectCostCodeID: worksheet.ProjectCostCodeID,
        UseCostCodes: worksheet.UseCostCodes
      };
    });
  }

  async function getWorksheetsAssociatedToFitters(fitterIds: number[]) {

    const requestOptions: RequestOptions = {
      method: 'post',
      body: fitterIds
    };

    const url = 'V2/Worksheet/GetWorksheetsAssociatedToFitters';

    return await request<WorksheetFilterResponseDTO[]>(url, requestOptions);
  }

  async function searchWorksheetsGrouped(requestBody: SearchWorksheetsRequestDTO) {
    const url = 'V3/Worksheet/SearchWorksheetsGrouped';

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<WorksheetSearchContainerDTO[]>(url, requestOptions);
  }

  async function getWorksheetsGroupedByProjectId(requestBody: GetWorksheetsGroupedByProjectIdRequestDTO) {
    const url = 'V2/Worksheet/GetWorksheetsGroupedByProjectId';

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<WorksheetSearchContainerDTO>(url, requestOptions);
  }

  async function getWorksheets(requestBody: WorksheetListRequestDTO) {
    const url = 'V3/Worksheet/GetWorksheets';

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<WorksheetListResponseContainerDTO>(url, requestOptions);
  }

  async function getWorksheetListByIDs(requestBody: WorksheetListByIDsRequestDTO) {

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    const url = 'V3/Worksheet/GetWorksheetListByIDs';

    return await request<WorksheetListResponseDTO[]>(url, requestOptions);
  }

  async function getWorksheetByID(worksheetId: number) {
    const url = 'V3/Worksheet/GetByID?worksheetId=' + worksheetId;

    return await request<WorksheetViewResponseDTO>(url);
  }

  async function getWorksheetCounts(requestBody: WorksheetCountsRequestDTO) {
    const url = 'V3/Worksheet/GetWorksheetCounts';

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<WorksheetCountsResponseDTO>(url, requestOptions);
  }

  async function getEmployeesOnWorksheet(worksheetId: number) {
    const url = 'V2/Worksheet/GetEmployeesOnWorksheet?worksheetId=' + worksheetId;

    return await request<EmployeesOnWorksheetContainerDTO>(url);
  }

  async function getSecondaryStatuses(worksheetId: number) {
    const url = 'V2/Worksheet/GetSecondaryStatuses?worksheetId=' + worksheetId;

    return await request<WorksheetStatusContainerDTO>(url);
  }

  async function takeOwnership(requestBody: WorksheetOwnershipRequestDTO) {
    const url = 'V2/Worksheet/TakeOwnership';

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<WorksheetOwnershipResponseDTO>(url, requestOptions);
  }

  async function getAssociatedPerson(id: number) {
    const url = 'V2/Worksheet/associatedPerson?personId=' + id;

    return await request<AssociatedPeopleResponseDTO>(url);
  }

  async function changeStatus(requestBody: ChangeWorksheetStatusRequestDTO) {
    const url = 'V2/Worksheet/ChangeStatus';

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody
    };

    return await request<ChangeStatusResponseDTO>(url, requestOptions);
  }

  async function update(requestBody: UpdateWorksheetRequestDTO) {
    const url = 'V2/Worksheet/Update';

    const requestOptions: RequestOptions = {
      method: 'post',
      body: requestBody,
      suppressToasts: true
    };

    return await request<boolean>(url, requestOptions);
  }

  return {
    errors,
    getWorksheetsAssignedToFitter,
    getWorksheetsAssociatedToFitters,
    searchWorksheetsGrouped,
    getWorksheetsGroupedByProjectId,
    getWorksheets,
    getWorksheetByID,
    getWorksheetListByIDs,
    getWorksheetCounts,
    getEmployeesOnWorksheet,
    getSecondaryStatuses,
    takeOwnership,
    getAssociatedPerson,
    changeStatus,
    update
  };
}
